import React, { useState, useEffect } from 'react';
import { API_URL } from '../config';
import axios from 'axios';
import { getSavedTemplates, getTemplateById, generateNewsFromReport, applyReportTemplate } from '../utils/reportTemplateUtils';
import { useSimpleDataLoader } from '../hooks/useSimpleDataLoader';

const MediaAdminPage = () => {
  const { allData, loading: dataLoading, fieldMetadata } = useSimpleDataLoader();
  const [allContent, setAllContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [savedTemplates, setSavedTemplates] = useState([]);

  // Состояние формы создания контента
  const [formData, setFormData] = useState({
    title: '',
    text: '',
    mediaFile: null,
    contentType: 'news', // 'tv' или 'news'
    priority: 5,
    displayTime: 10,
    allowedRoles: [],
    visible: true,
    useReportTemplate: false,
    selectedTemplateId: null
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchAllContent();
    fetchAvailableRoles();
    loadSavedTemplates();

    // Проверяем, есть ли данные отчета для создания новости
    checkPendingReportNews();
  }, []);

  // Загружаем сохраненные шаблоны
  const loadSavedTemplates = () => {
    const templates = getSavedTemplates();
    setSavedTemplates(templates);
  };

  // Проверяем наличие ожидающих данных отчета
  const checkPendingReportNews = () => {
    try {
      const pendingData = localStorage.getItem('pendingReportForNews');
      if (pendingData) {
        const reportData = JSON.parse(pendingData);

        // Автоматически заполняем форму данными из отчета
        if (reportData.templateName && reportData.result) {
          const newsContent = generateNewsFromReport(
            { name: reportData.templateName, description: 'Автоматически сгенерированный отчет' },
            reportData.result
          );

          setFormData(prev => ({
            ...prev,
            title: newsContent.title,
            text: newsContent.text,
            useReportTemplate: true,
            selectedTemplateId: reportData.templateId
          }));

          setShowCreateModal(true);

          // Удаляем данные после использования
          localStorage.removeItem('pendingReportForNews');

          alert('Форма автоматически заполнена данными из отчета!');
        }
      }
    } catch (error) {
      console.error('Ошибка обработки данных отчета:', error);
    }
  };

  const fetchAllContent = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      // Получаем все новости
      const newsResponse = await axios.get(`${API_URL}/api/news`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      console.log('News response:', newsResponse.data);

      // Получаем настройки новостей
      const settingsResponse = await axios.get(`${API_URL}/api/news/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      console.log('Settings response:', settingsResponse.data);

      // Получаем медиа файлы
      const mediaResponse = await axios.get(`${API_URL}/api/media`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      console.log('Media response:', mediaResponse.data);

      console.log('Response statuses:', {
        news: newsResponse.data.success,
        settings: settingsResponse.data.success,
        media: mediaResponse.data.success
      });

      if (newsResponse.data.success && settingsResponse.data.success && mediaResponse.data.success) {
        console.log('Processing news data, count:', newsResponse.data.data?.length);
        const newsWithSettings = newsResponse.data.data.map(news => {
          // Нормализуем данные для совместимости разных форматов
          const normalizedNews = {
            ...news,
            // Если text не существует, используем content
            text: news.text || news.content,
            // Если type не существует, определяем по наличию mediaFile
            type: news.type || (news.mediaFile ? 'media' : 'text'),
            // Используем настройки из news если они есть, иначе из settingsResponse
            settings: news.settings || settingsResponse.data.data[news.id] || {
              visible: true,
              priority: 5,
              displayTime: 10,
              allowedRoles: [],
              contentType: 'news'
            },
            itemType: 'content'
          };

          // Нормализуем mediaFile: преобразуем строку в объект если нужно
          if (normalizedNews.mediaFile && typeof normalizedNews.mediaFile === 'string') {
            const urlPath = normalizedNews.mediaFile;
            const filename = urlPath.split('/').pop();
            // Проверяем, является ли URL уже полным (начинается с http)
            const fullUrl = urlPath.startsWith('http') ? urlPath : `${API_URL}${urlPath}`;
            normalizedNews.mediaFile = {
              filename: filename,
              originalName: filename,
              url: fullUrl,
              type: filename.match(/\.(mp4|webm|mov)$/i) ? 'video/mp4' : 'image/jpeg',
              size: 0
            };
          } else if (normalizedNews.mediaFile && normalizedNews.mediaFile.url) {
            // Если mediaFile уже объект, проверяем URL
            if (!normalizedNews.mediaFile.url.startsWith('http') && !normalizedNews.mediaFile.url.startsWith(API_URL)) {
              normalizedNews.mediaFile.url = `${API_URL}${normalizedNews.mediaFile.url}`;
            }
          }

          return normalizedNews;
        });

        // Получаем список медиафайлов, которые уже используются в новостях
        const usedMediaFiles = newsWithSettings
          .filter(news => news.mediaFile)
          .map(news => typeof news.mediaFile === 'object' ? news.mediaFile.filename : news.mediaFile.split('/').pop());

        // Показываем только медиафайлы, которые НЕ используются в новостях
        const standAloneMedia = mediaResponse.data.data
          .filter(media => !usedMediaFiles.includes(media.filename))
          .map(media => ({
            ...media,
            itemType: 'media',
            title: media.filename,
            createdAt: media.uploadedAt
          }));

        const allItems = [...newsWithSettings, ...standAloneMedia]
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

        console.log('All items to display:', allItems);
        console.log('Setting allContent with', allItems.length, 'items');
        setAllContent(allItems);
      } else {
        console.error('One or more API responses failed:', {
          news: newsResponse.data.success,
          settings: settingsResponse.data.success,
          media: mediaResponse.data.success
        });
      }
    } catch (err) {
      console.error('Error fetching content:', err);
      console.error('Error details:', err.response?.data || err.message);
      setError('Ошибка загрузки контента');
    } finally {
      console.log('Fetch complete, loading set to false');
      setLoading(false);
    }
  };

  const fetchAvailableRoles = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/user-roles`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        setAvailableRoles(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching roles:', err);
    }
  };

  const handleCreateContent = async () => {
    if (!formData.title.trim()) {
      alert('Введите заголовок');
      return;
    }

    if (!formData.text.trim() && !formData.mediaFile) {
      alert('Введите текст или выберите медиафайл');
      return;
    }

    try {
      setCreating(true);
      const token = localStorage.getItem('token');

      let newsId;
      let mediaFileUrl = null;
      let mediaFileInfo = null;

      // Если есть медиафайл, сначала загружаем его
      if (formData.mediaFile) {
        const formDataToSend = new FormData();
        formDataToSend.append('mediaFile', formData.mediaFile);

        const uploadResponse = await axios.post(`${API_URL}/api/news/media`, formDataToSend, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });

        if (!uploadResponse.data.success) {
          throw new Error('Ошибка загрузки медиафайла');
        }

        mediaFileUrl = uploadResponse.data.url;
        mediaFileInfo = {
          type: uploadResponse.data.type,
          originalName: uploadResponse.data.originalName,
          filename: uploadResponse.data.filename,
          size: uploadResponse.data.size
        };
      }

      // Создаем новость (текстовую или с медиафайлом)
      // Используем startDate и endDate для совместимости с API
      const today = new Date().toISOString().split('T')[0];
      const endDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]; // +30 дней

      const response = await axios.post(`${API_URL}/api/news/send`, {
        title: formData.title,
        content: formData.text || formData.title, // content обязателен для API
        text: formData.text,
        startDate: today,
        endDate: endDate,
        mediaFile: mediaFileUrl,
        mediaFileInfo: mediaFileInfo,
        type: 'news'
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success && response.data.news) {
        newsId = response.data.news.id;
      } else {
        console.error('Не удалось получить ID новости из ответа:', response.data);
        throw new Error('Не удалось получить ID созданной новости');
      }

      // Устанавливаем настройки
      await axios.put(`${API_URL}/api/news/${newsId}/settings`, {
        visible: formData.visible,
        priority: formData.priority,
        displayTime: formData.contentType === 'tv' ? formData.displayTime : 10,
        allowedRoles: formData.allowedRoles,
        contentType: formData.contentType
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      alert('Контент успешно создан!');
      setShowCreateModal(false);
      resetForm();
      fetchAllContent();
    } catch (err) {
      console.error('Error creating content:', err);
      alert(err.response?.data?.message || err.message || 'Ошибка создания контента');
    } finally {
      setCreating(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      text: '',
      mediaFile: null,
      contentType: 'news',
      priority: 5,
      displayTime: 10,
      allowedRoles: [],
      visible: true,
      useReportTemplate: false,
      selectedTemplateId: null
    });
  };

  // Обработка выбора шаблона отчета
  const handleTemplateSelection = async (templateId) => {
    if (!templateId || !allData || dataLoading) {
      return;
    }

    try {
      const template = getTemplateById(parseInt(templateId));
      if (!template) {
        alert('Шаблон не найден');
        return;
      }

      // Применяем шаблон к данным
      const reportResult = applyReportTemplate(allData, template);

      // Генерируем контент для новости
      const newsContent = generateNewsFromReport(template, reportResult);

      // Обновляем форму
      setFormData(prev => ({
        ...prev,
        title: newsContent.title,
        text: newsContent.text,
        selectedTemplateId: parseInt(templateId)
      }));

      alert(`Контент сгенерирован на основе шаблона "${template.name}"`);
    } catch (error) {
      console.error('Ошибка применения шаблона:', error);
      alert('Ошибка при применении шаблона: ' + error.message);
    }
  };

  const handleDeleteContent = async (item) => {
    if (!confirm(`Удалить "${item.title || item.filename}"?`)) return;

    try {
      const token = localStorage.getItem('token');

      if (item.itemType === 'media') {
        // Удаляем медиафайл
        await axios.delete(`${API_URL}/api/media/${item.filename}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
      } else {
        // Удаляем новость
        await axios.delete(`${API_URL}/api/news/${item.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
      }

      alert('Контент удален!');
      fetchAllContent();
    } catch (err) {
      console.error('Error deleting content:', err);
      alert('Ошибка удаления контента');
    }
  };

  const handleEditContent = (item) => {
    if (item.itemType === 'media') {
      alert('Редактирование медиафайлов пока не поддерживается');
      return;
    }

    setEditingItem(item);
    setFormData({
      title: item.title || '',
      text: item.text || item.content || '',
      mediaFile: null,
      contentType: item.settings?.contentType || 'news',
      priority: item.settings?.priority || 5,
      displayTime: item.settings?.displayTime || 10,
      allowedRoles: item.settings?.allowedRoles || [],
      visible: item.settings?.visible !== false,
      useReportTemplate: false,
      selectedTemplateId: null
    });
    setShowEditModal(true);
  };

  const handleUpdateContent = async () => {
    if (!editingItem) return;

    try {
      setCreating(true);
      const token = localStorage.getItem('token');

      // Обновляем настройки новости
      await axios.put(`${API_URL}/api/news/${editingItem.id}/settings`, {
        visible: formData.visible,
        priority: formData.priority,
        displayTime: formData.contentType === 'tv' ? formData.displayTime : 10,
        allowedRoles: formData.allowedRoles,
        contentType: formData.contentType
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      alert('Настройки обновлены!');
      setShowEditModal(false);
      setEditingItem(null);
      resetForm();
      fetchAllContent();
    } catch (err) {
      console.error('Error updating content:', err);
      alert(err.response?.data?.message || err.message || 'Ошибка обновления контента');
    } finally {
      setCreating(false);
    }
  };

  const handleRoleChange = (roleValue, checked) => {
    setFormData(prev => ({
      ...prev,
      allowedRoles: checked
        ? [...prev.allowedRoles, roleValue]
        : prev.allowedRoles.filter(r => r !== roleValue)
    }));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('ru-RU');
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-xl text-gray-600">Загрузка...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-gray-800">Управление контентом</h1>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
            >
              <span className="mr-2">+</span>
              Создать запись
            </button>
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-red-700">{error}</p>
            </div>
          )}

          {/* Список всего контента */}
          <div className="space-y-4">
            {allContent.length === 0 ? (
              <p className="text-gray-500 text-center py-8">Нет созданного контента</p>
            ) : (
              allContent.map((item) => (
                <div key={`${item.itemType}-${item.id || item.filename}`}
                     className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <h3 className="font-medium text-gray-800">
                          {item.title || item.filename}
                        </h3>
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          item.itemType === 'media' ? 'bg-green-100 text-green-800' :
                          item.type === 'media' ? 'bg-purple-100 text-purple-800' :
                          'bg-blue-100 text-blue-800'
                        }`}>
                          {item.itemType === 'media' ? 'Медиафайл' :
                           item.type === 'media' ? 'Медиановость' : 'Текст'}
                        </span>
                        {item.settings?.contentType === 'tv' && (
                          <span className="px-2 py-1 text-xs bg-orange-100 text-orange-800 rounded-full">
                            ТВ
                          </span>
                        )}
                        {item.settings?.visible && (
                          <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                            Видимый
                          </span>
                        )}
                      </div>

                      {item.text && (
                        <p className="text-gray-600 mb-2 line-clamp-2">{item.text}</p>
                      )}

                      {/* Показываем медиафайл если есть */}
                      {item.mediaFile && (
                        <div className="mb-2">
                          <p className="text-sm text-gray-500 mb-1">Медиафайл:</p>
                          <div className="flex items-center space-x-2">
                            {item.mediaFile.type.startsWith('image/') ? (
                              <img
                                src={item.mediaFile.url}
                                alt={item.mediaFile.originalName}
                                className="w-16 h-16 object-cover rounded border"
                              />
                            ) : (
                              <video
                                src={item.mediaFile.url}
                                className="w-16 h-16 object-cover rounded border"
                                muted
                              />
                            )}
                            <span className="text-sm text-gray-600">{item.mediaFile.originalName}</span>
                          </div>
                        </div>
                      )}

                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <span>Создано: {formatDate(item.createdAt || item.uploadedAt)}</span>
                        {item.size && <span>Размер: {formatFileSize(item.size)}</span>}
                        {item.settings?.priority && (
                          <span>Приоритет: {item.settings.priority}</span>
                        )}
                        {item.settings?.displayTime && item.settings?.contentType === 'tv' && (
                          <span>Время показа: {item.settings.displayTime}с</span>
                        )}
                      </div>

                      {item.settings?.allowedRoles?.length > 0 && (
                        <div className="mt-2">
                          <span className="text-sm text-gray-500">Роли: </span>
                          {item.settings.allowedRoles.map(role => {
                            const roleInfo = availableRoles.find(r => r.value === role);
                            return (
                              <span key={role} className="inline-block px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded mr-1">
                                {roleInfo?.label || role}
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    <div className="flex space-x-2">
                      {item.itemType === 'media' && item.isActive && (
                        <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                          Активный
                        </span>
                      )}
                      {item.itemType !== 'media' && (
                        <button
                          onClick={() => handleEditContent(item)}
                          className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                        >
                          Редактировать
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteContent(item)}
                        className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                      >
                        Удалить
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Модальное окно создания контента */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-800">Создать новую запись</h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="space-y-4">
              {/* Заголовок */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Заголовок *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Введите заголовок..."
                />
              </div>

              {/* Текст */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Текст
                </label>
                <textarea
                  value={formData.text}
                  onChange={(e) => setFormData(prev => ({ ...prev, text: e.target.value }))}
                  rows={4}
                  className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Введите текст записи..."
                />
              </div>

              {/* Медиафайл */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Медиафайл (опционально)
                </label>
                <input
                  type="file"
                  accept="image/*,video/*"
                  onChange={(e) => setFormData(prev => ({ ...prev, mediaFile: e.target.files[0] }))}
                  className="w-full p-2 border border-gray-300 rounded-md"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Поддерживаются изображения и видео. Максимум 50MB.
                </p>
              </div>

              {/* Шаблоны отчетов */}
              <div className="border-t pt-4">
                <div className="flex items-center mb-3">
                  <input
                    type="checkbox"
                    checked={formData.useReportTemplate}
                    onChange={(e) => setFormData(prev => ({ ...prev, useReportTemplate: e.target.checked }))}
                    className="mr-2"
                  />
                  <label className="text-sm font-medium text-gray-700">
                    Создать новость на основе шаблона отчета
                  </label>
                </div>

                {formData.useReportTemplate && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Выберите шаблон отчета
                    </label>
                    <div className="flex space-x-2">
                      <select
                        value={formData.selectedTemplateId || ''}
                        onChange={(e) => {
                          const templateId = e.target.value;
                          setFormData(prev => ({ ...prev, selectedTemplateId: templateId ? parseInt(templateId) : null }));
                          if (templateId) {
                            handleTemplateSelection(templateId);
                          }
                        }}
                        className="flex-1 p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={dataLoading}
                      >
                        <option value="">-- Выберите шаблон --</option>
                        {savedTemplates.map(template => (
                          <option key={template.id} value={template.id}>
                            {template.name}
                          </option>
                        ))}
                      </select>
                      {dataLoading && (
                        <div className="flex items-center px-3 py-2 bg-gray-100 rounded-md">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                          <span className="text-xs text-gray-600">Загрузка данных...</span>
                        </div>
                      )}
                    </div>
                    {savedTemplates.length === 0 && (
                      <p className="text-xs text-amber-600 mt-1">
                        Шаблоны отчетов не найдены. Создайте их в разделе "Отчеты → Конструктор отчетов".
                      </p>
                    )}
                    {formData.selectedTemplateId && (
                      <p className="text-xs text-green-600 mt-1">
                        ✅ Контент будет автоматически сгенерирован на основе выбранного шаблона
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Тип контента */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Тип контента
                </label>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="news"
                      checked={formData.contentType === 'news'}
                      onChange={(e) => setFormData(prev => ({ ...prev, contentType: e.target.value }))}
                      className="mr-2"
                    />
                    Страница новостей
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="tv"
                      checked={formData.contentType === 'tv'}
                      onChange={(e) => setFormData(prev => ({ ...prev, contentType: e.target.value }))}
                      className="mr-2"
                    />
                    ТВ дашборд
                  </label>
                </div>
              </div>

              {/* Приоритет */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Приоритет (1-10)
                </label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={formData.priority}
                  onChange={(e) => setFormData(prev => ({ ...prev, priority: parseInt(e.target.value) }))}
                  className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Время показа (только для ТВ) */}
              {formData.contentType === 'tv' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Время показа на ТВ (секунды)
                  </label>
                  <input
                    type="number"
                    min="3"
                    max="60"
                    value={formData.displayTime}
                    onChange={(e) => setFormData(prev => ({ ...prev, displayTime: parseInt(e.target.value) }))}
                    className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              )}

              {/* Видимость */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.visible}
                  onChange={(e) => setFormData(prev => ({ ...prev, visible: e.target.checked }))}
                  className="mr-2"
                />
                <label className="text-sm font-medium text-gray-700">
                  Сделать видимым сразу
                </label>
              </div>

              {/* Выбор ролей */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Кто может видеть
                </label>
                <div className="max-h-32 overflow-y-auto border border-gray-300 rounded-md p-2">
                  <label className="flex items-center space-x-2 py-1">
                    <input
                      type="checkbox"
                      checked={formData.allowedRoles.length === 0}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData(prev => ({ ...prev, allowedRoles: [] }));
                        }
                      }}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm font-medium">Все пользователи</span>
                  </label>
                  {availableRoles.map((role) => (
                    <label key={role.value} className="flex items-center space-x-2 py-1">
                      <input
                        type="checkbox"
                        checked={formData.allowedRoles.includes(role.value)}
                        onChange={(e) => handleRoleChange(role.value, e.target.checked)}
                        className="rounded border-gray-300"
                      />
                      <span className="text-sm">
                        {role.label}
                        {role.type === 'custom' && (
                          <span className="text-xs text-blue-600 ml-1">(пользовательская)</span>
                        )}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Кнопки */}
            <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Отмена
              </button>
              <button
                onClick={handleCreateContent}
                disabled={creating}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center"
              >
                {creating && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                )}
                {creating ? 'Создание...' : 'Создать'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно редактирования */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">
                Редактировать настройки новости
              </h2>

              <div className="space-y-4">
                {/* Заголовок (только для просмотра) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Заголовок (только для просмотра)
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600"
                  />
                </div>

                {/* Текст (только для просмотра) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Текст (только для просмотра)
                  </label>
                  <textarea
                    value={formData.text}
                    disabled
                    rows="4"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600"
                  />
                </div>

                {/* Тип контента */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Тип контента
                  </label>
                  <select
                    value={formData.contentType}
                    onChange={(e) => setFormData({ ...formData, contentType: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="news">Страница новостей</option>
                    <option value="tv">ТВ дашборд</option>
                  </select>
                </div>

                {/* Приоритет */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Приоритет (1-10)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Время показа (только для ТВ) */}
                {formData.contentType === 'tv' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Время показа (секунд)
                    </label>
                    <input
                      type="number"
                      min="5"
                      max="60"
                      value={formData.displayTime}
                      onChange={(e) => setFormData({ ...formData, displayTime: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                )}

                {/* Разрешенные роли */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Разрешенные роли
                  </label>
                  <div className="space-y-2">
                    {['admin', 'director', 'manager', 'worker'].map(role => (
                      <label key={role} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.allowedRoles.includes(role)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({
                                ...formData,
                                allowedRoles: [...formData.allowedRoles, role]
                              });
                            } else {
                              setFormData({
                                ...formData,
                                allowedRoles: formData.allowedRoles.filter(r => r !== role)
                              });
                            }
                          }}
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-700">{role}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Видимость */}
                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.visible}
                      onChange={(e) => setFormData({ ...formData, visible: e.target.checked })}
                      className="mr-2"
                    />
                    <span className="text-sm font-medium text-gray-700">Видимый контент</span>
                  </label>
                </div>
              </div>

              {/* Кнопки */}
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingItem(null);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                >
                  Отмена
                </button>
                <button
                  onClick={handleUpdateContent}
                  disabled={creating}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center"
                >
                  {creating && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  )}
                  {creating ? 'Сохранение...' : 'Сохранить'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MediaAdminPage;